import React from "react";
import { graphql, PageProps } from "gatsby";
import { HomepageQuery } from "types/graphql-types";
import { MosaicLayout } from "@silversea-ssc/ui-components-react";
import DestinationCard from "components/Card/DestinationCard";
import {
  SectionContainer,
  SectionDivider
} from "@silversea-ssc/ui-components-react";
import PageBuilderVideoSection from "components/PageBuilder/PageBuilderVideoSection";
import PageBuilderSectionTitle from "components/PageBuilder/PageBuilderSectionTitle";
import PageBuilderPageSlider from "components/PageBuilder/PageBuilderPageSlider";
import PageBuilderCollectionSlider from "components/PageBuilder/PageBuilderCollectionSlider";
import FindYourCruiseSearchBox from "components/FindYourCruiseSearchBox/FindYourCruiseSearchBox";
import OffersSlider from "components/CollectionSlider/OffersSlider";
import HeroBannerSlideshow from "components/HeroBannerSlideshow/HeroBannerSlideshow";
import OpenGraph from "components/SEO/OpenGraph";
import PageBuilderButtonCTA from "components/PageBuilder/PageBuilderButtonCTA";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { Trans } from "@lingui/macro";
import showExchangeDisclaimer from "src/utils/showExchangeDisclaimer";
import { isPresent } from "src/utils/checks";

const ExchangeDisclaimer = () => {
  const geoLocation = useGeolocation();
  const exchangeDisclaimer = showExchangeDisclaimer(geoLocation);

  return (
    <div>
      {exchangeDisclaimer && (
        <Trans id="exchange-rate-disclaimer-all-voyages">
          Favourable CAD/USD exchange rate (1 USD = 1.25 CAD) applies across all
          voyages
        </Trans>
      )}
    </div>
  );
};

const HomepageTemplate: React.FC<PageProps<HomepageQuery>> = ({
  data,
  location
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const page = data.page!;
  const heroImages = page.heroImages?.filter(isPresent) || [];

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={data.page?.metadata?.openGraph}
        excludeFromSitemap={false}
      />
      <HeroBannerSlideshow
        slides={heroImages}
        newsMessage={page.newsMessage?.localized}
      />

      <FindYourCruiseSearchBox />

      <SectionContainer
        spacingTop={undefined}
        spacingBottom="regular"
        backgroundColor="white"
        dataContentName="exclusive_offers"
      >
        <PageBuilderSectionTitle {...page.offersHeading}>
          <ExchangeDisclaimer />
        </PageBuilderSectionTitle>
        <OffersSlider location={location} cardBackgroundColor="neutral05" />
      </SectionContainer>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="neutral05"
        dataContentName="destinations"
      >
        <PageBuilderSectionTitle
          {...page.featuredDestinationsHeading}
          ctaButtons={[]}
        />
        <MosaicLayout variant="column3" data-content-name="destinations_mosaic">
          {page.featuredDestinations?.map((item) => {
            if (!item) return null;
            return (
              <DestinationCard
                key={item._id}
                title={item.card?.title?.localized || ""}
                image={item.card?.image}
                destinationId={item.destinationId}
                url={item.fullPath}
              />
            );
          })}
        </MosaicLayout>
        {page.featuredDestinationsHeading?.ctaButtons &&
          page.featuredDestinationsHeading?.ctaButtons?.length > 0 && (
            <SectionDivider
              backgroundColorTop="neutral05"
              backgroundColorBottom="neutral05"
              spacingTop="compact"
            >
              {page.featuredDestinationsHeading?.ctaButtons.map((b) => (
                <PageBuilderButtonCTA key={b?._key} {...b} />
              ))}
            </SectionDivider>
          )}
      </SectionContainer>

      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="titanium"
        dataContentName="video_section"
      >
        <PageBuilderVideoSection {...page.video} />
      </SectionContainer>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="neutral05"
        dataContentName="ships"
      >
        <PageBuilderCollectionSlider
          heading={page.shipsHeading}
          collection="ships"
          cardBackgroundColor="white"
          location={location}
        />
      </SectionContainer>
      <SectionContainer
        spacingTop="regular"
        spacingBottom="regular"
        backgroundColor="white"
        dataContentName="signature_cruises"
      >
        <PageBuilderPageSlider
          {...page.signatureCruises}
          cardBackgroundColor="neutral05"
        />
      </SectionContainer>
    </React.Fragment>
  );
};

export const query = graphql`
  query Homepage($id: String!) {
    page: sanityHomepage(id: { eq: $id }) {
      metadata {
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      newsMessage {
        localized
      }
      heroImages {
        ...PageBuilderSanityHeroSlideContent
      }

      featuredDestinationsHeading {
        ...SectionTitle
        ...SectionTitleCTAs
      }

      featuredDestinations {
        _id
        destinationId
        fullPath
        metadata {
          tracking {
            name
          }
        }
        card {
          ...AsCard
        }
      }

      offersHeading {
        ...SectionTitle
        ...SectionTitleCTAs
      }
      shipsHeading {
        ...SectionTitle
        ...SectionTitleCTAs
      }

      signatureCruises {
        heading {
          ...SectionTitle
          ...SectionTitleCTAs
        }
        items {
          ...PageAsCard
        }
      }

      video {
        ...PageBuilderVideoSection
      }
    }
  }
`;

export default HomepageTemplate;
